/**
 * PEValuationPanel — independent P/E comparison chart for the Stock
 * Analysis Multi-Benchmark section.  Charts the stock's Trailing /
 * Forward / Current / Blended P/E, PEG ratio, TTM profit margin, its
 * industry-ETF top-10 peers (individually toggleable), the industry
 * average, plus its own regression line and P/E moving averages.
 * EPS backbone: SEC 10-K/10-Q quarterly filings.  Includes an AI read
 * that judges cheap vs rich from this exact data.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";
import { RefreshCw, Eye, EyeOff, Sparkles, ExternalLink } from "lucide-react";
import { renderWithLinks } from "./AIAnalysisChat";
import { API } from "../config/api";

export const PE_TOGGLES = [
  { key: "pe_trailing", label: "Trailing P/E", color: "#22d3ee" },
  { key: "pe_forward", label: "Forward P/E", color: "#f59e0b" },
  { key: "pe_current", label: "Current P/E", color: "#f8fafc" },
  { key: "pe_blended", label: "Blended P/E", color: "#a78bfa" },
  { key: "pe_peg", label: "PEG Ratio", color: "#fb7185" },
  { key: "pe_margin", label: "Profit Margin", color: "#84cc16" },
  { key: "pe_peers", label: "Top-10 Peers", color: "#94a3b8" },
  { key: "pe_avg", label: "Industry Avg", color: "#34d399" },
];

const PEER_COLORS = ["#f472b6", "#fb923c", "#facc15", "#4ade80", "#2dd4bf",
                     "#60a5fa", "#c084fc", "#fb7185", "#a3e635", "#38bdf8"];

const fmtX = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}×`);

/* Small pill chip used for in-panel controls (peers, regression, MAs) */
const Chip = ({ on, color, label, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border transition-colors
      ${on ? "bg-slate-800 border-slate-500 text-white" : "bg-slate-900 border-slate-700 text-slate-500 hover:text-slate-200"}`}
    style={on ? { boxShadow: `inset 0 0 0 1.5px ${color}` } : {}}
  >
    {on ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
    {label}
  </button>
);

const rollingMean = (rows, key, win) => rows.map((_, i) => {
  if (i + 1 < win) return null;
  let s = 0, n = 0;
  for (let j = i - win + 1; j <= i; j++) {
    const v = rows[j][key];
    if (v != null) { s += v; n++; }
  }
  return n >= win * 0.7 ? Math.round((s / n) * 100) / 100 : null;
});

const linearReg = (rows, key) => {
  const pts = rows.map((r, i) => [i, r[key]]).filter(p => p[1] != null);
  if (pts.length < 3) return () => null;
  const n = pts.length;
  const sx = pts.reduce((a, p) => a + p[0], 0);
  const sy = pts.reduce((a, p) => a + p[1], 0);
  const sxy = pts.reduce((a, p) => a + p[0] * p[1], 0);
  const sxx = pts.reduce((a, p) => a + p[0] * p[0], 0);
  const denom = n * sxx - sx * sx;
  if (!denom) return () => null;
  const m = (n * sxy - sx * sy) / denom;
  const b = (sy - m * sx) / n;
  return (i) => Math.round((m * i + b) * 100) / 100;
};

export default function PEValuationPanel({ ticker, timeframe, onValuation }) {
  const [visible, setVisible] = useState({
    pe_trailing: true, pe_forward: false, pe_current: false, pe_blended: false,
    pe_peg: false, pe_margin: false, pe_peers: true, pe_avg: true,
  });
  const toggleVis = (k) => setVisible(v => ({ ...v, [k]: !v[k] }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [peerOn, setPeerOn] = useState({});
  const [showReg, setShowReg] = useState(false);
  const [showMa20, setShowMa20] = useState(false);
  const [showMa50, setShowMa50] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiCitations, setAiCitations] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const aiFiredRef = useRef(null);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/stock/pe-comparison/${ticker}`, {
      params: { timeframe }, timeout: 120000,
    })
      .then(r => {
        if (cancelled) return;
        if (r.data?.error && !(r.data?.chart_data || []).length) {
          setErr(r.data.error);
          if (retryTick === 0) setTimeout(() => !cancelled && setRetryTick(1), 6000);
          return;
        }
        setData(r.data);
        setErr(null);
        if (r.data?.partial && retryTick < 2) {
          setTimeout(() => !cancelled && setRetryTick(t => t + 1), 8000);
        }
        const init = {};
        (r.data?.peers || []).forEach(p => { init[p] = true; });
        setPeerOn(prev => Object.keys(prev).length ? prev : init);
      })
      .catch(e => {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || e.message);
        if (retryTick === 0) setTimeout(() => !cancelled && setRetryTick(1), 6000);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker, timeframe, retryTick]);

  useEffect(() => { setPeerOn({}); setAiText(null); setAiCitations([]); }, [ticker]);

  const rows = useMemo(() => {
    const cd = data?.chart_data || [];
    if (!cd.length) return cd;
    const reg = linearReg(cd, "pe_trailing");
    const ma20 = rollingMean(cd, "pe_trailing", 20);
    const ma50 = rollingMean(cd, "pe_trailing", 50);
    return cd.map((r, i) => ({ ...r, pe_reg: reg(i), pe_ma20: ma20[i], pe_ma50: ma50[i] }));
  }, [data]);

  const askAi = () => {
    setAiLoading(true);
    setAiText(null);
    setAiCitations([]);
    axios.post(`${API}/chart-ai/pe-read`, { ticker, timeframe }, { timeout: 90000 })
      .then(r => {
        setAiText(r.data?.answer || "No answer.");
        setAiCitations(r.data?.citations || []);
      })
      .catch(e => setAiText(`AI read failed: ${e?.response?.data?.detail || e.message}`))
      .finally(() => setAiLoading(false));
  };

  // Auto-load the AI Read as soon as valuation data lands (once per
  // ticker+timeframe — backend caches the read 30 min, so refetches are cheap).
  useEffect(() => {
    if (!data || loading) return;
    const key = `${ticker}|${timeframe}`;
    if (aiFiredRef.current === key) return;
    aiFiredRef.current = key;
    askAi();
  }, [data, loading, ticker, timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compact valuation summary for the AI chat (Dashboard passes it into
  // AIAnalysisChat's context so answers are grounded in the panel's data).
  const valuationSummary = useMemo(() => {
    if (!data?.chart_data?.length) return null;
    if (data.ticker && data.ticker !== ticker) return null; // stale data during ticker switch
    const c = data.current || {};
    const pes = data.chart_data.map(p => p.pe_trailing).filter(v => v != null).sort((a, b) => a - b);
    return {
      ticker, timeframe,
      industry_etf: data.industry_etf, industry_label: data.industry_label,
      current: {
        trailing: c.trailing, forward: c.forward, blended: c.blended,
        peg: c.peg, profit_margin: c.profit_margin, eps_growth: c.eps_growth,
        industry_avg: c.industry_avg,
      },
      peer_current: data.peer_current || {},
      pe_history: pes.length ? {
        min: pes[0], median: pes[Math.floor(pes.length / 2)], max: pes[pes.length - 1],
      } : null,
      partial: !!data.partial,
    };
  }, [data, ticker, timeframe]);

  useEffect(() => {
    if (!onValuation) return;
    if (!valuationSummary) { onValuation(null); return; }
    const ai = aiText && !aiText.startsWith("AI read failed") ? aiText.slice(0, 900) : null;
    onValuation({ ...valuationSummary, ai_read: ai });
  }, [valuationSummary, aiText]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ticker || ticker === "MKTAVG") return null;

  const peers = data?.peers || [];
  const cur = data?.current || {};
  const enoughFor50 = rows.length >= 50;
  const enoughFor20 = rows.length >= 20;

  return (
    <div className="border border-slate-700 rounded-lg p-3 bg-slate-900/40" data-testid="pe-valuation-panel">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="text-xs font-bold text-white uppercase tracking-wider">
          P/E Valuation — {ticker}
        </span>
        {data?.industry_etf && (
          <span className="text-[10px] text-slate-400">
            vs {data.industry_etf}{data.industry_label ? ` · ${data.industry_label}` : ""} top-10 by weight
          </span>
        )}
        {loading && <RefreshCw className="w-3 h-3 animate-spin text-cyan-400" />}
        {data?.partial && !loading && (
          <button
            onClick={() => setRetryTick(t => t + 1)}
            data-testid="pe-partial-retry"
            className="px-1.5 py-0.5 rounded border border-amber-600/60 text-amber-300 hover:bg-amber-900/30 text-[9px] font-semibold"
          >
            some peers missing — retry
          </button>
        )}
        <span className="ml-auto flex items-center gap-2 font-mono text-[10px] flex-wrap">
          {cur.trailing != null && <span className="text-cyan-300" data-testid="pe-cur-trailing">Trailing {fmtX(cur.trailing)}</span>}
          {cur.forward != null && <span className="text-amber-300" data-testid="pe-cur-forward">Fwd {fmtX(cur.forward)}</span>}
          {cur.blended != null && <span className="text-violet-300" data-testid="pe-cur-blended">Blend {fmtX(cur.blended)}</span>}
          {cur.peg != null && <span className="text-rose-300" data-testid="pe-cur-peg">PEG {Number(cur.peg).toFixed(2)}</span>}
          {cur.profit_margin != null && <span className="text-lime-300" data-testid="pe-cur-margin">Margin {Number(cur.profit_margin).toFixed(1)}%</span>}
          {cur.industry_avg != null && <span className="text-emerald-300" data-testid="pe-cur-avg">Industry {fmtX(cur.industry_avg)}</span>}
        </span>
      </div>
      {cur.trailing != null && cur.industry_avg != null && (
        <div className="text-[10px] text-slate-300 mb-2" data-testid="pe-verdict">
          {ticker} trades at <span className="font-mono font-bold">{fmtX(cur.trailing)}</span> vs the industry's{" "}
          <span className="font-mono font-bold">{fmtX(cur.industry_avg)}</span> —{" "}
          <span className={cur.trailing > cur.industry_avg ? "text-rose-300 font-semibold" : "text-emerald-300 font-semibold"}>
            {cur.trailing > cur.industry_avg
              ? `${((cur.trailing / cur.industry_avg - 1) * 100).toFixed(0)}% ABOVE the industry average (relatively expensive)`
              : `${((1 - cur.trailing / cur.industry_avg) * 100).toFixed(0)}% BELOW the industry average (relatively cheap)`}
          </span>
        </div>
      )}

      {/* Valuations toggles — control which P/E lines render */}
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5" data-testid="pe-valuations-toggles">
        <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Valuations</span>
        {PE_TOGGLES.map(t => (
          <Chip key={t.key} on={visible[t.key]} color={t.color} label={t.label}
                testid={`mbc-toggle-${t.key}`} onClick={() => toggleVis(t.key)} />
        ))}
      </div>

      {/* In-panel analytics controls: regression + P/E moving averages + AI read */}
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Analytics</span>
        <Chip on={showReg} color="#e879f9" label="Regression" testid="pe-toggle-regression"
              onClick={() => setShowReg(v => !v)} />
        <Chip on={showMa20} color="#fde047" label={enoughFor20 ? "P/E MA20" : "P/E MA20 (n/a)"} testid="pe-toggle-ma20"
              onClick={() => enoughFor20 && setShowMa20(v => !v)} />
        <Chip on={showMa50} color="#f97316" label={enoughFor50 ? "P/E MA50" : "P/E MA50 (n/a)"} testid="pe-toggle-ma50"
              onClick={() => enoughFor50 && setShowMa50(v => !v)} />
        <button
          onClick={askAi}
          disabled={aiLoading || !data}
          data-testid="pe-ai-read-btn"
          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-cyan-700 bg-cyan-950/60 text-cyan-200 hover:bg-cyan-900/60 transition-colors disabled:opacity-50"
        >
          {aiLoading ? <RefreshCw className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
          AI Read — cheap or rich?
        </button>
      </div>

      {/* Individual peer toggles */}
      {visible.pe_peers && peers.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-2" data-testid="pe-peer-chips">
          <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Peers</span>
          {peers.map((p, i) => (
            <Chip key={p} on={peerOn[p] !== false} color={PEER_COLORS[i % PEER_COLORS.length]}
                  label={`${p}${data?.peer_current?.[p] != null ? ` ${fmtX(data.peer_current[p])}` : ""}`}
                  testid={`pe-peer-toggle-${p}`}
                  onClick={() => setPeerOn(prev => ({ ...prev, [p]: prev[p] === false }))} />
          ))}
          <button
            onClick={() => {
              const allOn = peers.every(p => peerOn[p] !== false);
              const next = {};
              peers.forEach(p => { next[p] = !allOn; });
              setPeerOn(next);
            }}
            data-testid="pe-peer-toggle-all"
            className="text-[9px] uppercase text-slate-400 hover:text-white underline underline-offset-2"
          >
            {peers.every(p => peerOn[p] !== false) ? "hide all" : "show all"}
          </button>
        </div>
      )}

      {aiLoading && !aiText && (
        <div className="flex items-center gap-2 border border-cyan-900/70 bg-cyan-950/30 rounded-md p-2 mb-2 text-[11px] text-cyan-200"
             data-testid="pe-ai-read-loading">
          <RefreshCw className="w-3 h-3 animate-spin" />
          AI Read — analyzing whether {ticker} is cheap or rich…
        </div>
      )}
      {aiText && (
        <div className="border border-cyan-900/70 bg-cyan-950/30 rounded-md p-2 mb-2 text-[11px] leading-snug text-slate-100 whitespace-pre-wrap"
             data-testid="pe-ai-read-text">
          {renderWithLinks(aiText)}
          {aiCitations.length > 0 && (
            <div className="mt-2 pt-1.5 border-t border-cyan-900/50 space-y-0.5" data-testid="pe-ai-read-sources">
              <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Sources</div>
              {aiCitations.map((c, i) => (
                <a key={i} href={c.url} target="_blank" rel="noopener noreferrer"
                   data-testid={`pe-ai-read-source-${i}`}
                   className="flex items-center gap-1 text-cyan-300 hover:text-cyan-200 hover:underline">
                  <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                  <span className="truncate">
                    <span className="uppercase text-[9px] opacity-70 mr-1">{c.source_type}</span>
                    {c.title}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="flex items-center gap-2 text-[11px] text-rose-300 mb-1">
          <span>{err}</span>
          <button
            onClick={() => setRetryTick(t => t + 1)}
            data-testid="pe-retry-btn"
            className="px-2 py-0.5 rounded border border-rose-500/60 text-rose-200 hover:bg-rose-900/40 text-[10px] font-semibold"
          >
            Retry
          </button>
        </div>
      )}
      {loading && !data ? (
        <div className="h-[360px] flex items-center justify-center text-slate-400 text-xs">
          Pulling SEC EPS filings + peer prices… (first load can take ~20s)
        </div>
      ) : rows.length > 0 ? (
        <div className="overflow-x-auto overflow-y-hidden" data-testid="pe-chart-scroll">
          <div style={{ minWidth: "640px", height: 380 }}>
            <ResponsiveContainer width="100%" height={380}>
              <LineChart data={rows} margin={{ top: 6, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="time" tick={{ fill: "#94a3b8", fontSize: 10 }} minTickGap={40} />
                <YAxis yAxisId="pe" tick={{ fill: "#94a3b8", fontSize: 10 }} width={44}
                       domain={["auto", "auto"]}
                       label={{ value: "P/E ×", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }} />
                {visible.pe_margin && (
                  <YAxis yAxisId="pct" orientation="right" tick={{ fill: "#84cc16", fontSize: 10 }} width={40}
                         domain={["auto", "auto"]} tickFormatter={(v) => `${v}%`} />
                )}
                {visible.pe_peg && (
                  <YAxis yAxisId="peg" hide
                         domain={[(min) => Math.min(0, min * 0.5), (max) => max * 2 + 0.5]} />
                )}
                <Tooltip
                  contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 11 }}
                  labelStyle={{ color: "#e2e8f0" }}
                  formatter={(v, name) => {
                    if (name.includes("Margin")) return [`${Number(v).toFixed(1)}%`, name];
                    if (name.includes("PEG")) return [Number(v).toFixed(2), name];
                    return [fmtX(v), name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {visible.pe_trailing && (
                  <Line yAxisId="pe" type="monotone" dataKey="pe_trailing" name={`${ticker} Trailing P/E`}
                        stroke="#22d3ee" strokeWidth={2.4} dot={false} connectNulls />
                )}
                {visible.pe_forward && (
                  <Line yAxisId="pe" type="monotone" dataKey="pe_forward" name={`${ticker} Forward P/E`}
                        stroke="#f59e0b" strokeWidth={1.8} strokeDasharray="6 3" dot={false} connectNulls />
                )}
                {visible.pe_current && (
                  <Line yAxisId="pe" type="monotone" dataKey="pe_current" name={`${ticker} Current P/E`}
                        stroke="#f8fafc" strokeWidth={1.4} dot={false} connectNulls />
                )}
                {visible.pe_blended && (
                  <Line yAxisId="pe" type="monotone" dataKey="pe_blended" name={`${ticker} Blended P/E`}
                        stroke="#a78bfa" strokeWidth={1.8} strokeDasharray="4 2" dot={false} connectNulls />
                )}
                {visible.pe_peg && (
                  <Line yAxisId="peg" type="monotone" dataKey="peg" name={`${ticker} PEG`}
                        stroke="#fb7185" strokeWidth={1.8} strokeDasharray="2 2" dot={false} connectNulls />
                )}
                {visible.pe_margin && (
                  <Line yAxisId="pct" type="monotone" dataKey="profit_margin" name={`${ticker} Net Margin %`}
                        stroke="#84cc16" strokeWidth={1.8} dot={false} connectNulls />
                )}
                {showReg && (
                  <Line yAxisId="pe" type="monotone" dataKey="pe_reg" name="P/E Regression"
                        stroke="#e879f9" strokeWidth={1.6} strokeDasharray="9 4" dot={false} connectNulls />
                )}
                {showMa20 && (
                  <Line yAxisId="pe" type="monotone" dataKey="pe_ma20" name="P/E MA20"
                        stroke="#fde047" strokeWidth={1.4} dot={false} connectNulls />
                )}
                {showMa50 && (
                  <Line yAxisId="pe" type="monotone" dataKey="pe_ma50" name="P/E MA50"
                        stroke="#f97316" strokeWidth={1.4} dot={false} connectNulls />
                )}
                {visible.pe_peers && peers.filter(p => peerOn[p] !== false).map((p) => (
                  <Line key={p} yAxisId="pe" type="monotone" dataKey={`peer_${p}`} name={p}
                        stroke={PEER_COLORS[peers.indexOf(p) % PEER_COLORS.length]} strokeWidth={1.1}
                        strokeOpacity={0.85} dot={false} connectNulls />
                ))}
                {visible.pe_avg && (
                  <Line yAxisId="pe" type="monotone" dataKey="industry_avg" name="Industry Avg P/E"
                        stroke="#34d399" strokeWidth={2.6} strokeDasharray="8 4" dot={false} connectNulls />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : !loading ? (
        <div className="text-[11px] text-slate-400 py-6 text-center">
          No P/E data available for {ticker} (EPS may be negative or unreported).
        </div>
      ) : null}
      {data?.eps_sources && (
        <div className="text-[9px] text-slate-500 mt-1">
          EPS source: SEC 10-K/10-Q quarterly filings{Object.values(data.eps_sources).includes("stockanalysis-flat")
            ? " · flat-EPS fallback used for tickers without USD SEC data" : ""} · Forward P/E uses today's consensus forward EPS across historical prices · PEG = trailing P/E ÷ TTM EPS growth
        </div>
      )}
    </div>
  );
}
